import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import Loader from "../Common/Loader";
import { FaMicrophone } from "react-icons/fa";

import {
  addObjectionAction,
  updateObjectionAction,
} from "../../../store/actions/ObjectionAction";

import { showErrorAlert } from "../../../services/ErrorHandlingService";

const AddObjection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const objection = location.state?.item || null;
  const isEditMode = !!objection;

  const [form, setForm] = useState({
    objection_text: "",
    objection_status: "Active",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const recognitionRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [speechLang, setSpeechLang] = useState("en-IN");

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  // ================= LOAD DATA (EDIT) =================
  useEffect(() => {
    if (isEditMode && objection) {
      setForm({
        objection_text: objection.objection_text || "",
        objection_status:
          objection.objection_status ||
          (objection.is_active ? "Active" : "Inactive"),
      });
    }
  }, [isEditMode, objection]);
  

  // ================= SPEECH =================
  const toggleLanguage = () => {
    setSpeechLang((prev) => (prev === "en-IN" ? "mr-IN" : "en-IN"));
  };

  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech recognition not supported");
      return;
    }

    if (recognitionRef.current) return;

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = speechLang;

    setIsRecording(true);

    recognition.onresult = (event) => {
      const text = event.results[0][0].transcript;
      setForm((prev) => {
        const nextValue = prev.objection_text
          ? prev.objection_text + " " + text
          : text;
        clearFieldError("objection_text");
        return { ...prev, objection_text: nextValue };
      });
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setIsRecording(false);
    };

    recognition.start();
  };

  const handleStatusChange = (selectedOption) => {
    handleFieldChange(
      "objection_status",
      selectedOption ? selectedOption.value : ""
    );
  };


  // ================= VALIDATION =================
  const validate = () => {
    const newErrors = {};

    if (!form.objection_text.trim())
      newErrors.objection_text = "Objection is required";

    if (!form.objection_status)
      newErrors.objection_status = "Status is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleFieldChange = (field, value) => {
    if (field === "objection_text") {
      setForm((prev) => ({ ...prev, objection_text: value }));
    } else if (field === "objection_status") {
      setForm((prev) => ({ ...prev, objection_status: value }));
    }
    clearFieldError(field);
  };

  // ================= SUBMIT =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      if (isEditMode) {
        await dispatch(
          updateObjectionAction(
            {
              objection_text: form.objection_text,
              objection_status: form.objection_status,
            },
            objection.objection_id
          )
        );
      } else {
        await dispatch(
          addObjectionAction({
            objection_text: form.objection_text,
            objection_status: form.objection_status,
          })
        );
      }

      navigate("/objections");
    } catch (error) {
      console.error(error);
      showErrorAlert("Failed to save objection");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="d-flex justify-content-end mb-3">
          <Link to="/objections">
            <Button variant="primary">Back</Button>
          </Link>
        </div>

        <div className="card">
          <div className="card-body">
            {/* <h4 className="mb-4">
              {isEditMode ? "Edit Objection" : "Add Objection"}
            </h4> */}

            <form onSubmit={handleSubmit}>
              {/* Objection Text */}
              <div className="mb-3 position-relative">
                <label className="form-label">
                  Objection <span className="text-danger">*</span>
                </label>

                <textarea
                  className="form-control"
                  rows="4"
                  value={form.objection_text}
                  style={{ padding: "0.375rem 0.75rem", paddingTop: "0.6rem" }}
                  onChange={(e) =>
                    handleFieldChange("objection_text", e.target.value)
                  }
                />

                <button
                  type="button"
                  onClick={startListening}
                  disabled={isRecording}
                  style={{
                    position: "absolute",
                    right: "10px",
                    bottom: "10px",
                    borderRadius: "50%",
                    border: "none",
                    background: isRecording ? "#ff4d4d" : "#eee",
                    width: "34px",
                    height: "34px",
                  }}
                >
                  <FaMicrophone />
                </button>

                {errors.objection_text && (
                  <div className="invalid-feedback d-block">
                    {errors.objection_text}
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="mb-3">
                <label className="form-label">
                  Status <span className="text-danger">*</span>
                </label>

                <Select
                  options={statusOptions}
                  value={statusOptions.find(
                    (opt) => opt.value === form.objection_status
                  )}
                  onChange={handleStatusChange}
                  placeholder="Select Status"
                  isClearable
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "50px",
                      height: "50px",
                      borderColor: errors.objection_status ? "#dc3545" : provided.borderColor,
                      boxShadow: errors.objection_status
                        ? "0 0 0 0.2rem rgba(220,53,69,.25)"
                        : provided.boxShadow,
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      lineHeight: "50px",
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      padding: "4px",
                    }),
                    indicatorSeparator: () => ({
                      display: "none",
                    }),
                  }}
                />

                {errors.objection_status && (
                  <small className="text-danger">{errors.objection_status}</small>
                )}
              </div>


              {/* Submit */}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader size="sm" />{" "}
                    {isEditMode ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  isEditMode ? "Update Objection" : "Add Objection"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddObjection;
